
import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { GalleryImage } from './gallery-image';
import { slideVariants } from './animations';
import type { ImageType } from './types';

interface GalleryProps {
  images: ImageType[];
  currentIndex: number;
  direction: number;
  totalSlides: number;
  normalizedIndex: number;
  imagesPerSlide: number;
  onPrevious: () => void;
  onNext: () => void;
  onImageClick: (image: ImageType) => void;
  onSlideChange: (index: number) => void;
}

export const Gallery = ({
  images,
  currentIndex,
  direction,
  totalSlides,
  normalizedIndex,
  imagesPerSlide,
  onPrevious,
  onNext,
  onImageClick,
  onSlideChange,
}: GalleryProps) => {
  const getImagesForSlide = (index: number) => {
    const normalizedIdx = ((index % totalSlides) + totalSlides) % totalSlides;
    const startIdx = normalizedIdx * imagesPerSlide;
    return images.slice(startIdx, startIdx + imagesPerSlide).length === imagesPerSlide 
      ? images.slice(startIdx, startIdx + imagesPerSlide)
      : [...images.slice(startIdx), ...images.slice(0, imagesPerSlide - (images.length - startIdx))];
  };

  const visibleImages = getImagesForSlide(currentIndex);

  const handleDragEnd = (e: any, { offset }: { offset: { x: number } }) => {
    if (offset.x < -50) {
      onNext();
    } else if (offset.x > 50) {
      onPrevious();
    }
  };

  return (
    <>
      <div className="relative overflow-hidden">
        <AnimatePresence initial={false} mode="wait" custom={direction}>
          <motion.div 
            key={currentIndex}
            custom={direction}
            variants={slideVariants}
            initial={direction > 0 ? "hiddenRight" : "hiddenLeft"}
            animate="visible"
            exit={direction > 0 ? "exitLeft" : "exitRight"}
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6"
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={1}
            onDragEnd={handleDragEnd}
          >
            {visibleImages.map((image, index) => (
              <GalleryImage
                key={`${normalizedIndex}-${index}`}
                image={image}
                onClick={() => onImageClick(image)}
              />
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
      
      <div className="flex justify-center items-center mt-8 gap-2">
        <button
          onClick={onPrevious}
          className="p-1 hover:bg-primary/10 rounded-full transition-colors"
          aria-label="Previous slide"
        >
          <ChevronLeft size={18} className="text-primary" />
        </button>
        
        {Array.from({ length: totalSlides }).map((_, index) => (
          <button
            key={index}
            onClick={() => {
              onSlideChange(index);
            }}
            className={cn(
              "w-2.5 h-2.5 rounded-full transition-all",
              normalizedIndex === index ? "bg-primary w-5" : "bg-gray-300 hover:bg-gray-400"
            )}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
        
        <button
          onClick={onNext}
          className="p-1 hover:bg-primary/10 rounded-full transition-colors"
          aria-label="Next slide"
        >
          <ChevronRight size={18} className="text-primary" />
        </button>
      </div>
    </>
  );
};
